import React, { useEffect, useState } from 'react';

import { Grid } from '@material-ui/core';
import Helmet from 'react-helmet';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useHistory } from 'react-router-dom';

import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { authModel, passwordModel } from 'models';
import { ROUTES } from 'routes/constants';
import { getQueryParams, getTWithPathKey } from 'utils';
import {
  BuilderAuthCard,
  BuilderTextValidator,
  BuilderStyledButton,
  BuilderTitle,
  BuilderSubtitle,
  Box,
} from 'pages/auth/style';
import { ButtonComponent } from 'components';
import { COLORS } from 'theme/constants';
import { PasswordChecklist } from 'components/PasswordChecklist/PasswordChecklist';
import { withSuccessActionsDialog } from 'utils/enhancers';
import { useSubmittingState } from 'hooks';
import { redirectToSignIn } from 'utils/helpers/urlHelpers';

import BuilderSignIn from '../BuilderSignIn/BuilderSignIn';

interface BuilderCreatePasswordProps {
  setSuccessActionMessages: (messages: string[]) => void;
}

function BuilderCreatePassword({ setSuccessActionMessages }: BuilderCreatePasswordProps) {
  const [passwordReset, setPasswordReset] = useState(false);
  const { withHandlingSubmittingState, isSubmitting } = useSubmittingState();
  const [state, setState] = useState({
    password: '',
    passwordConfirmation: '',
    resetPasswordToken: '',
  });
  const { handleServerError } = useServerErrorActions();
  const getT = getTWithPathKey('pages.auth.createPassword');
  const history = useHistory();

  useEffect(() => {
    const resetPasswordToken = getQueryParams().reset_password_token as string;

    passwordModel
      .verifyPasswordReset({ resetPasswordToken })
      .then(() => {
        setState(prevState => ({ ...prevState, resetPasswordToken }));
      })
      .catch(() => {
        history.push(ROUTES.linkExpired);
      });
  }, []);

  const handlePost = withHandlingSubmittingState(() =>
    passwordModel
      .updatePassword(state)
      .then(() => {
        setPasswordReset(true);
      })
      .catch(handleServerError),
  );

  const handleChange = (item: Record<string, string>) =>
    setState(prevState => ({
      ...prevState,
      ...item,
    }));

  const disableResendButton = Object.values(state).some(value => !value);

  if (passwordReset) {
    const BuilderSignInWithMessage = withSuccessActionsDialog(BuilderSignIn);

    return (
      <BuilderSignInWithMessage
        title="Success!"
        message="Your password was successfully updated! Please Log In to continue."
        setSuccessActionMessages={setSuccessActionMessages}
      />
    );
  }

  return (
    <BuilderAuthCard>
      <Helmet title={getT('tabTitle')} />
      <Grid container direction="column" alignItems="center" spacing={6}>
        <Grid item xl={7} style={{ flex: 1, width: '100%' }}>
          <Box mb={6}>
            <ButtonComponent
              color="primary"
              variant="text"
              className="no-padding"
              text="< Back"
              onClick={redirectToSignIn}
              style={{ color: COLORS.builderBrandColor }}
            />
          </Box>
          <BuilderTitle>Set new Password</BuilderTitle>
          <Box mt={4} mb={8}>
            <BuilderSubtitle>Choose a strong password</BuilderSubtitle>
          </Box>
        </Grid>
        <Grid item xl={7} style={{ flex: 1 }}>
          <ValidatorForm onSubmit={handlePost}>
            <Box mb={3}>
              <PasswordChecklist open={state.password.length > 0} password={state.password}>
                <BuilderTextValidator
                  validators={[VALIDATORS_MAP.required]}
                  label={getT('passwordInputLabel')}
                  value={state.password}
                  fieldName={authModel.fields.password}
                  handleChange={handleChange}
                  type="password"
                  disableMinHeight
                />
              </PasswordChecklist>
            </Box>
            <BuilderTextValidator
              validators={[VALIDATORS_MAP.required]}
              label="Repeat new password"
              value={state.passwordConfirmation}
              fieldName={authModel.fields.passwordConfirmation}
              handleChange={handleChange}
              type="password"
              disableMinHeight
            />
            <Box mt={5}>
              <BuilderStyledButton
                fullWidth
                color="primary"
                type="submit"
                text="Set new password"
                disabled={disableResendButton}
                loading={isSubmitting}
              />
            </Box>
          </ValidatorForm>
        </Grid>
      </Grid>
    </BuilderAuthCard>
  );
}

export default BuilderCreatePassword;
